import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import ConfirmHost from './components/ConfirmHost';
import { getDefaultPage, roleLabels, rolePages, pagesFor, isFlaggedPage } from './config/rolePages';
import { useAppFlags } from './appFlags';
import { api } from './api';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import ProductsPage from './pages/ProductsPage';
import BouquetsPage from './pages/BouquetsPage';
import OrdersPage from './pages/OrdersPage';
import CalendarPage from './pages/CalendarPage';
import ClientsPage from './pages/ClientsPage';
import SettingsPage from './pages/SettingsPage';
import BroadcastPage from './pages/BroadcastPage';
import PackagingPage from './pages/PackagingPage';
import EmployeesPage from './pages/EmployeesPage';
import FloristOrdersPage from './pages/FloristOrdersPage';
import PackerOrdersPage from './pages/PackerOrdersPage';
import CourierDeliveriesPage from './pages/CourierDeliveriesPage';
import CourierAssignmentPage from './pages/CourierAssignmentPage';
import AnalyticsPage from './pages/AnalyticsPage';
import WarehousePage from './pages/WarehousePage';
import ProcurementPage from './pages/ProcurementPage';
import CertificatesPage from './pages/CertificatesPage';
import FlowwowPage from './pages/FlowwowPage';
import ReceivingPage from './pages/ReceivingPage';
import Toast from './components/Toast';

// Раздел — в адресе браузера (/orders, /calendar…): работают «Назад», закладки и ссылка в новой
// вкладке (UX-аудит 25.10). nginx отдаёт index.html на любой путь; чужой раздел роль-проверка ниже заменит
const pageFromPath = () => {
  const p = window.location.pathname.replace(/^\/+|\/+$/g, '');
  return /^[a-z-]+$/.test(p) ? p : null;
};

function AppContent() {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  const [activePage, setActivePage] = useState(() => {
    return pageFromPath() || localStorage.getItem('stems_active_page') || null;
  });
  const [toasts, setToasts] = useState([]);
  // Модули магазина (закупка с биржи) — пункты меню с flag; null — ещё грузятся
  const flags = useAppFlags(isAuthenticated);
  const [narrowHeader, setNarrowHeader] = useState(() => window.innerWidth < 420);
  useEffect(() => {
    const on = () => setNarrowHeader(window.innerWidth < 420);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  // Счётчик на вкладке «Приёмка»: открытые поставки; освежаем при смене раздела и раз в минуту
  const [receivingCount, setReceivingCount] = useState(0);
  const hasReceiving = !!user && pagesFor(user.role, flags).some(p => p.id === 'receiving');
  useEffect(() => {
    if (!hasReceiving) return undefined;
    let alive = true;
    const load = () => api.getReceivingInvoices()
      .then(r => { if (alive) setReceivingCount((r.receiving || []).length); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [hasReceiving, activePage]);

  // Страница из localStorage — от ПРЕДЫДУЩЕЙ сессии на этом устройстве, и роль
  // у неё могла быть другой: админ закрыл браузер на «Заказах», токен протух
  // без «Выхода», следом вошёл курьер — и получал админские «Заказы» в своей
  // шапке с нулями (эндпоинты отвечают 403). Поэтому сохранённую страницу
  // сверяем с ролью: чужую заменяем на стартовую для роли.
  const pageFitsRole = (page) => {
    if (!user?.role || !page) return false;
    const staffPages = ['florist-orders', 'packer-orders', 'courier-deliveries'];
    const isAdminRole = user.role === 'admin' || user.role === 'owner';
    // У админа есть и legacy-страницы вне меню; неизвестный адрес (/order — опечатка) — на стартовую,
    // а не пустой экран, который ещё и запоминался (ревью 26.10)
    const legacy = ['packaging', 'procurement', 'statistics', 'workload', 'funnel'];
    return isAdminRole
      ? !staffPages.includes(page) && [...rolePages.owner.map(p => p.id), ...legacy].includes(page)
      : pagesFor(user.role, flags).map(p => p.id).includes(page);
  };
  useEffect(() => {
    if (!user?.role) return;
    // Раздел модуля («Приёмка»), а флаги ещё не пришли — подождать, а не выкинуть на стартовую
    if (flags === null && isFlaggedPage(user.role, activePage)) return;
    if (!pageFitsRole(activePage)) {
      setActivePage(getDefaultPage(user.role));
    }
  }, [user, activePage, flags]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activePage) {
      localStorage.setItem('stems_active_page', activePage);
      const path = `/${activePage}`;
      if (window.location.pathname !== path) {
        // первый заход («/» или чужой раздел) — заменяем, дальше — новая запись для «Назад»
        const replace = !pageFromPath() || !window.history.state?.nfPage;
        window.history[replace ? 'replaceState' : 'pushState']({ nfPage: activePage }, '', path + window.location.search);
      } else if (!window.history.state?.nfPage) {
        window.history.replaceState({ nfPage: activePage }, '', window.location.href);
      }
    }
  }, [activePage]);

  // «Назад» / «Вперёд» браузера — раздел из адреса
  useEffect(() => {
    const onPop = () => { const p = pageFromPath(); if (p) setActivePage(p); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    // Ошибки бывают с инструкцией («пусть нажмёт «Старт», затем…») — их держим дольше
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, type === 'error' ? 8000 : 3000);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('stems_active_page');
    // адрес раздела — тоже сбросить: иначе следующий вход (другая роль) открывался бы с чужим разделом
    window.history.replaceState(null, '', '/');
    logout();
    setActivePage(null);
  };

  const renderPage = () => {
    // Чужой для роли раздел (адрес из прошлой сессии, ссылка админа у флориста) не рисуем даже на
    // мгновение до замены: его запросы получали 403 — «Ошибка загрузки заказов» у флориста (26.10)
    if (!pageFitsRole(activePage)) return null;
    switch (activePage) {
      // Admin pages
      case 'products':
        return <ProductsPage showToast={showToast} />;
      case 'packaging':
        return <PackagingPage showToast={showToast} />;
      case 'orders':
        return <OrdersPage showToast={showToast} />;
      case 'calendar':
        return <CalendarPage showToast={showToast} />;
      case 'courier-assignment':
        return <CourierAssignmentPage showToast={showToast} />;
      case 'procurement':
        return <ProcurementPage showToast={showToast} />;
      case 'warehouse':
        return <WarehousePage showToast={showToast} />;
      case 'employees':
        return <EmployeesPage showToast={showToast} />;
      case 'users':
        return <ClientsPage showToast={showToast} />;
      case 'analytics':
      case 'statistics':    // legacy fallback
      case 'workload':      // legacy fallback
      case 'funnel':        // legacy fallback
      case 'procurement':   // moved to analytics tab
        // Переход из статистики в другой раздел (например, «Склад → Списания»): вкладка — разовой меткой
        return <AnalyticsPage showToast={showToast} onNavigate={(page, tab) => {
          if (tab) { try { sessionStorage.setItem(`nf_open_tab_${page}`, tab); } catch (e) { /* no-op */ } }
          setActivePage(page);
        }} />;
      case 'broadcast':
        // Рассылки — только владельцу (пункта нет в меню админа, это страховка)
        return user?.role === 'owner' ? <BroadcastPage showToast={showToast} /> : null;
      case 'certificates':
        return <CertificatesPage showToast={showToast} />;
      case 'flowwow':
        return <FlowwowPage showToast={showToast} />;
      case 'settings':
        return <SettingsPage showToast={showToast} />;
      // Role pages
      case 'florist-orders':
        return <FloristOrdersPage showToast={showToast} />;
      case 'packer-orders':
        return <PackerOrdersPage showToast={showToast} />;
      case 'courier-deliveries':
        return <CourierDeliveriesPage showToast={showToast} />;
      case 'receiving':
        return <ReceivingPage showToast={showToast} />;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="loading" style={{ height: '100vh' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <Login />
        <Toast toasts={toasts} />
      </>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const staffNav = isAdmin ? [] : pagesFor(user?.role, flags);

  // Admin uses sidebar layout; other roles use header layout
  if (isAdmin) {
    return (
      <div className="admin-layout">
        <Sidebar
          activePage={activePage}
          setActivePage={setActivePage}
          onLogout={handleLogout}
        />
        <main className="main-content">
          {renderPage()}
        </main>
        <Toast toasts={toasts} />
      </div>
    );
  }

  // Header layout for florist/packer/courier — бренд NovaFlo
  return (
    <div className="app">
      {/* Шапка АРМа на телефоне (390 px): имя в одну строку с многоточием, роль мелко под ним
          (уже 420 px — без роли), «Выход» — значком 44×44 (владелец 04.11) */}
      <header style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px 8px 16px', background: '#fff',
        borderBottom: '1px solid #EBEDEC',
      }}>
        <img src="/logo/novaflo-mark-v2.svg?v=2" alt="NovaFlo" style={{ height: 32, width: 32, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
          <div style={{ fontSize: 14, color: '#1C3D2E', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user?.name}
          </div>
          {!narrowHeader && (
            <div style={{ fontSize: 11, color: '#5E6561' }}>{roleLabels[user?.role] || user?.role}</div>
          )}
        </div>
        <button
          type="button"
          className="sidebar-logout"
          onClick={handleLogout}
          aria-label="Выход"
          title="Выход"
          style={{ margin: 0, padding: 0, width: 44, height: 44, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg className="sidebar-logout-icon" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M16 17L21 12L16 7"/>
            <path d="M21 12H9"/>
            <path d="M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7"/>
          </svg>
        </button>
      </header>
      <main style={{ padding: 16, maxWidth: 900, margin: '0 auto' }}>
        {/* Несколько разделов у роли (флорист с «Приёмкой») — переключатель над разделом */}
        {staffNav.length > 1 && (
          <div className="seg staff-tabs" style={{ marginBottom: 12 }}>
            {staffNav.map(p => (
              <button key={p.id} type="button" onClick={() => setActivePage(p.id)}
                className={`seg-btn${activePage === p.id ? ' on' : ''}`}>
                {p.label}
                {/* «Приёмка 1» — сколько поставок сейчас принимаем */}
                {p.id === 'receiving' && receivingCount > 0 && (
                  <span style={{ marginLeft: 6, padding: '0 6px', fontSize: 11, fontWeight: 700, background: activePage === p.id ? '#fff' : '#B0603C',
                    color: activePage === p.id ? '#1C3D2E' : '#fff', '--ch': '4px' }}>{receivingCount}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {renderPage()}
      </main>
      <Toast toasts={toasts} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
      <ConfirmHost />
    </AuthProvider>
  );
}

export default App;
